/**
 * InformePericialPDF.jsx
 *
 * Documento "Informe Pericial Técnico" generado por el admin.
 *
 * Estructura: usamos `<table>` con `<thead>` y `<tfoot>` porque Chrome
 * (y la mayoría de navegadores) repite automáticamente esos elementos
 * en cada página al imprimir, lo que nos da cabecera y pie de página
 * consistentes en todo el informe (incluso cuando el cuerpo se desborda
 * a múltiples páginas).
 */
import { forwardRef, useEffect, useState } from 'react';
import { empresaAPI } from '@/lib/api';

const InformePericialPDF = forwardRef(function InformePericialPDF(
  { orden, cliente, titulo, cuerpo, perito, fechaEmision },
  ref,
) {
  const [empresa, setEmpresa] = useState(null);
  useEffect(() => {
    empresaAPI.obtener().then(r => setEmpresa(r.data)).catch(() => {});
  }, []);

  const fmtDate = (d) => {
    const x = d ? new Date(d) : new Date();
    return x.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const logoSrc = (() => {
    const raw = empresa?.logo?.url || empresa?.logo_url;
    if (!raw) return null;
    if (typeof raw === 'string' && raw.startsWith('http')) return raw;
    return raw;
  })();

  // Cabecera repetida en cada página
  const HeaderRow = (
    <tr>
      <td style={{ padding: '15mm 15mm 5mm' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          borderBottom: '2px solid #111', paddingBottom: '4mm',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6mm' }}>
            {logoSrc && (
              <img src={logoSrc} alt="Logo"
                   style={{ height: '14mm', width: 'auto', objectFit: 'contain' }} />
            )}
            <div>
              <p style={{ fontSize: '12px', fontWeight: 700, margin: 0, color: '#111' }}>
                {empresa?.nombre || 'Revix.es'}
              </p>
              <p style={{ fontSize: '7.5px', color: '#555', margin: '0.5mm 0 0', lineHeight: 1.4 }}>
                {empresa?.direccion || ''}{empresa?.cif ? ` · CIF: ${empresa.cif}` : ''}
                {empresa?.telefono ? ` · Tel.: ${empresa.telefono}` : ''}
                {empresa?.email ? ` · ${empresa.email}` : ''}
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '9px', color: '#666', margin: 0,
                        textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
              Informe técnico
            </p>
            <p style={{ fontSize: '8px', color: '#999', margin: '1mm 0 0' }}>
              OT: {orden?.numero_orden || '—'} · Emitido: {fmtDate(fechaEmision)}
            </p>
          </div>
        </div>
      </td>
    </tr>
  );

  // Pie repetido en cada página
  const FooterRow = (
    <tr>
      <td style={{ padding: '4mm 15mm 10mm' }}>
        <div style={{
          borderTop: '1px solid #ddd', paddingTop: '2mm',
          fontSize: '7.5px', color: '#888', textAlign: 'center', lineHeight: 1.4,
        }}>
          <p style={{ margin: 0 }}>
            {empresa?.nombre || 'Revix.es'} · Documento técnico confidencial · OT {orden?.numero_orden || '—'}
          </p>
          <p style={{ margin: '0.8mm 0 0' }}>
            {empresa?.web || empresa?.email || ''}
            {empresa?.telefono ? ` · ${empresa.telefono}` : ''}
          </p>
        </div>
      </td>
    </tr>
  );

  return (
    <div ref={ref} style={{
      width: '210mm',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
      color: '#111', background: '#fff',
      pageBreakAfter: 'always', breakAfter: 'page',
    }} data-testid="informe-pericial-pdf">

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ display: 'table-header-group' }}>
          {HeaderRow}
        </thead>
        <tfoot style={{ display: 'table-footer-group' }}>
          {FooterRow}
        </tfoot>
        <tbody>
          <tr>
            <td style={{ padding: '0 15mm', verticalAlign: 'top' }}>

              {/* Título */}
              <div style={{ margin: '6mm 0 6mm' }}>
                <h1 style={{
                  fontSize: '24px', fontWeight: 800, letterSpacing: '-0.4px',
                  margin: 0, lineHeight: 1.15,
                }}>
                  {titulo || 'Informe técnico sobre el dispositivo'}
                </h1>
              </div>

              {/* Datos clave (no se rompe entre páginas) */}
              <section style={{
                background: '#F8F8F9', border: '1px solid #E5E5EA', borderRadius: '4px',
                padding: '4mm 5mm', marginBottom: '6mm', fontSize: '10.5px',
                pageBreakInside: 'avoid', breakInside: 'avoid',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3mm 8mm' }}>
                  <Field label="Orden de trabajo" value={orden?.numero_orden || '—'} mono />
                  <Field label="Estado" value={orden?.estado || '—'} />
                  <Field label="Cliente" value={cliente?.nombre || orden?.cliente_nombre || '—'} />
                  <Field label="Dispositivo" value={orden?.dispositivo?.modelo || '—'} />
                  <Field label="IMEI / N.º Serie" value={orden?.dispositivo?.imei || '—'} mono />
                  <Field label="Color" value={orden?.dispositivo?.color || '—'} />
                  <Field label="Fecha recepción" value={orden?.fecha_recibida_centro ? fmtDate(orden.fecha_recibida_centro) : '—'} />
                  <Field label="Técnico asignado" value={orden?.tecnico_nombre || orden?.tecnico_asignado || '—'} />
                </div>
              </section>

              {/* Cabecera del cuerpo (no se separa del primer párrafo) */}
              <h2 style={{
                fontSize: '13px', fontWeight: 700, margin: '0 0 3mm',
                borderBottom: '1px solid #E5E5EA', paddingBottom: '2mm',
                pageBreakAfter: 'avoid', breakAfter: 'avoid',
              }}>
                Análisis técnico
              </h2>

              {/* Cuerpo del informe — fluye entre páginas con header/footer
                  repetidos por la table */}
              <div style={{
                fontSize: '11px', lineHeight: 1.65, color: '#1d1d1f',
                whiteSpace: 'pre-wrap', textAlign: 'justify',
                hyphens: 'auto', wordBreak: 'break-word',
              }}>
                {cuerpo?.trim() || (
                  <em style={{ color: '#999' }}>
                    (No se introdujo texto del informe técnico.)
                  </em>
                )}
              </div>

              {/* Firma — bloque protegido contra corte */}
              <section style={{
                marginTop: '14mm',
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12mm',
                fontSize: '10px',
                pageBreakInside: 'avoid', breakInside: 'avoid',
              }}>
                <div>
                  <div style={{ borderTop: '1px solid #111', paddingTop: '2mm', marginTop: '14mm' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>
                      {perito || empresa?.nombre || 'Técnico responsable'}
                    </p>
                    <p style={{ margin: '0.5mm 0 0', color: '#666' }}>Técnico emisor</p>
                  </div>
                </div>
                <div>
                  <div style={{ borderTop: '1px solid #111', paddingTop: '2mm', marginTop: '14mm' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>{fmtDate(fechaEmision)}</p>
                    <p style={{ margin: '0.5mm 0 0', color: '#666' }}>Fecha de emisión</p>
                  </div>
                </div>
              </section>

              {/* Aviso final compacto */}
              <p style={{
                marginTop: '8mm', fontSize: '8px', color: '#888',
                textAlign: 'justify', lineHeight: 1.5,
                pageBreakInside: 'avoid', breakInside: 'avoid',
              }}>
                Este documento se emite a efectos técnicos sobre el dispositivo
                identificado y su contenido tiene carácter profesional. Se adjuntan a
                continuación, como anexos, la ficha técnica de la orden y el reportaje
                fotográfico cuando proceda.
              </p>

            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});

function Field({ label, value, mono }) {
  return (
    <div>
      <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', margin: 0 }}>
        {label}
      </p>
      <p style={{
        fontSize: '10.5px', fontWeight: 500, margin: '0.5mm 0 0', color: '#111',
        fontFamily: mono ? 'ui-monospace, Menlo, Consolas, monospace' : undefined,
      }}>
        {value}
      </p>
    </div>
  );
}

export default InformePericialPDF;
